import type { Item } from "@/types/api";

export function formatDurationMS(ms: number | null | undefined): string | null {
  if (!ms || ms <= 0) return null;
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function formatDurationMSOrZero(ms: number | null | undefined): string {
  return formatDurationMS(ms) ?? "0m";
}

export function formatEpisodeCode(item: Item): string | null {
  return formatEpisodeCodeFromNumbers(
    item.attributes.season_number,
    item.attributes.episode_number,
  );
}

export function formatEpisodeCodeFromNumbers(
  season: number | null | undefined,
  episode: number | null | undefined,
): string | null {
  if (season == null && episode == null) return null;
  if (season == null) return `E${String(episode).padStart(2, "0")}`;
  if (episode == null) return `S${String(season).padStart(2, "0")}`;
  return `S${String(season).padStart(2, "0")} E${String(episode).padStart(2, "0")}`;
}

export function formatSeasonLabel(seasonNumber: number | null | undefined): string {
  return seasonNumber ? `Season ${seasonNumber}` : "Specials";
}

// Metadata providers usually name a season after its number ("Season 3", or a
// localized equivalent), and only sometimes give it a real name — Heroes has
// "Villains / Fugitives" for season 3. A numeric name carries nothing the number
// does not, so it is dropped rather than repeated beside it.
// "Volume 3" is deliberately absent: Heroes numbers its volumes separately from
// its seasons, so a volume name is information the season number does not carry.
const GENERIC_SEASON_NAME =
  /^(?:season|series|saison|staffel|temporada|stagione|seizoen|sezon|sezona|s)\s*0*\d+$/i;

// A name that already leads with its own number ("Season 3: Villains") is trimmed
// back to the part the number does not already say.
function stripLeadingSeasonNumber(name: string, seasonNumber: number): string {
  const pattern = new RegExp(
    `^(?:season|series|saison|staffel|temporada|stagione|seizoen|sezon|sezona|s)\\s*0*${seasonNumber}\\s*[:\\-–—·•]\\s*`,
    "i",
  );
  return name.replace(pattern, "").trim();
}

export function seasonDistinctName(
  seasonNumber: number | null | undefined,
  seasonTitle: string | null | undefined,
): string | null {
  const trimmed = seasonTitle?.trim();
  if (!trimmed) return null;
  if (GENERIC_SEASON_NAME.test(trimmed)) return null;
  if (!seasonNumber && /^specials?$/i.test(trimmed)) return null;
  const name = seasonNumber ? stripLeadingSeasonNumber(trimmed, seasonNumber) : trimmed;
  if (!name || name === formatSeasonLabel(seasonNumber)) return null;
  return name;
}

export interface SeasonLabelParts {
  /** "Season 3", or "Specials" for season 0. Null when metadata has no number. */
  number: string | null;
  /** "Villains / Fugitives". Null when the name only restates the number. */
  name: string | null;
  /** Both parts joined for headings, card titles, and accessible names. */
  full: string;
}

export function formatSeasonLabelParts(
  seasonNumber: number | null | undefined,
  seasonTitle: string | null | undefined,
): SeasonLabelParts {
  const name = seasonDistinctName(seasonNumber, seasonTitle);
  // season 0 is "Specials", which identifies it as well as a number would;
  // a missing number leaves the name to identify the season on its own.
  if (seasonNumber == null) return { number: null, name, full: name ?? formatSeasonLabel(null) };
  const number = formatSeasonLabel(seasonNumber);
  return { number, name, full: name ? `${number} · ${name}` : number };
}

export function formatSeasonDisplayLabel(
  seasonNumber: number | null | undefined,
  seasonTitle: string | null | undefined,
): string {
  return formatSeasonLabelParts(seasonNumber, seasonTitle).full;
}
